// ChatArea.tsx

import React from "react";
import { MessageCircle, Phone, Video, Zap } from "lucide-react";
import { Conversation, IconType, Message } from "../Types";
import { Avatar } from "../Avatar";
import SimpleBar from "simplebar-react";
import ChatFooter from "./components/ChatFooter";

// --- Action Button Component (for Chat Header) ---
interface ActionButtonProps {
  icon: IconType;
  onClick: () => void;
  isActive?: boolean;
  label: string;
}

const ActionButton: React.FC<ActionButtonProps> = ({
  icon: Icon,
  onClick,
  isActive = false,
  label,
}) => {
  return (
    <button
      onClick={onClick}
      aria-label={label}
      title={label}
      className={`p-2 rounded-full transition-colors ${
        isActive
          ? "bg-blue-600 text-white"
          : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-blue-600"
      }`}
    >
      <Icon className="w-5 h-5" />
    </button>
  );
};

// --- Message Bubble Component ---
interface MessageBubbleProps {
  message: Message;
  onRetry?: (msg: any) => void;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({ message, onRetry }) => {
  const isUser = message.direction === "outbound";
  const isFailed = isUser && message.status === "failed";

  return (
    <div className={`flex flex-col ${isUser ? "items-end" : "items-start"}`}>
      {/* ----- BUBBLE ----- */}
      <div
        className={`max-w-xs md:max-w-md rounded-xl p-3 shadow-sm ${
          isUser
            ? "bg-blue-500 text-white rounded-br-md"
            : "bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white rounded-tl-md"
        }`}
      >
        <p className="text-sm break-words">{message.message}</p>
      </div>

      {/* ----- STATUS + TIME (below bubble) ----- */}
      <div className="flex items-center space-x-2 mt-1">
        {/* Status only for outbound */}
        {isUser && (
          <span
            className={`text-[11px] ${
              isFailed ? "text-red-500" : "text-gray-500 dark:text-gray-400"
            }`}
          >
            {message.status}
          </span>
        )}

        {/* Retry Button */}
        {isFailed && (
          <button
            onClick={() => onRetry?.(message)}
            className="text-[11px] text-red-600 underline hover:text-red-400"
          >
            Retry
          </button>
        )}

        {/* Time */}
        <span className="text-[11px] text-gray-400 dark:text-gray-500">
          {new Date(message.createdAt).toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          })}
        </span>
      </div>
    </div>
  );
};

// --- Chat Area Component ---
interface ChatAreaProps {
  activeConversation: Conversation | null;
  messages: Message[];
  isSidebarOpen: boolean;
  handleQuickActionsClick: () => void;
}

export const ChatArea: React.FC<ChatAreaProps> = ({
  activeConversation,
  messages,
  isSidebarOpen,
  handleQuickActionsClick,
}) => {
  // Dynamic width based on right sidebar state
  const chatAreaClasses = isSidebarOpen
    ? "max-w-[calc(100vw-80px-384px)]"
    : "max-w-full";

  return (
    <div className={`flex-1 flex flex-col ${chatAreaClasses}`}>
      {activeConversation ? (
        <>
          {/* Chat Header */}
          <div className="p-3 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex items-center justify-between shadow-sm">
            <div className="flex items-center space-x-3">
              <Avatar
                firstName={activeConversation?.contactId?.firstName}
                lastName={activeConversation?.contactId?.lastName}
                isActive={true}
                size="large"
              />
              <div>
                <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
                  {activeConversation?.contactId?.firstName}{" "}
                  {activeConversation?.contactId?.lastName}
                </h3>
                {/* <p className="text-sm text-gray-500 dark:text-gray-400">
                  View Contact
                </p> */}
              </div>
            </div>

            {/* Communication/Info Actions */}
            <div className="flex items-center space-x-1">
              <ActionButton
                icon={Phone}
                onClick={() => console.log("Call")}
                label="Call"
              />
              <ActionButton
                icon={Video}
                onClick={() => console.log("Video")}
                label="Video"
              />

              {/* QUICK ACTIONS BUTTON */}
              <ActionButton
                icon={Zap}
                onClick={handleQuickActionsClick}
                isActive={isSidebarOpen} // Active if the whole quick actions panel is open
                label="Contact Actions"
              />
            </div>
          </div>

          {/* Messages Area */}
          <div className="h-full max-h-[calc(100vh_-_330px)] xl:max-h-[calc(100vh_-_400px)] overflow-y-auto bg-gray-50 dark:bg-gray-900">
            <div className="flex-1 p-6 space-y-4 ">
              {messages.map((message) => (
                <MessageBubble
                  key={message._id}
                  message={message}
                  onRetry={(failedMsg) => {
                    console.log("Retrying...", failedMsg);
                  }}
                />
              ))}
            </div>
          </div>
          {/* Message Input */}
          <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
            <ChatFooter
              activeCoversation={activeConversation}
              contact={activeConversation?.contactId}
            />
          </div>
        </>
      ) : (
        <div className="flex-1 flex items-center justify-center bg-gray-50 dark:bg-gray-900">
          <div className="text-center p-8">
            <MessageCircle className="w-16 h-16 text-blue-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 dark:text-gray-300">
              Select a conversation to start chatting
            </h3>
          </div>
        </div>
      )}
    </div>
  );
};
